import {HorizontalTextAlign, TextWidgetData, VerticalTextAlign} from '@dv5/domain'
import React, {CSSProperties, FC, useEffect, useRef, useState} from 'react'
import {isModPressed} from '@dv5/components'
import {ColorPicker} from '../common/ColorPicker'
import {TextAlignmentPicker} from '../common/TextAlignmentPicker'
import {TextFormatPicker} from './TextFormatPicker'
import {COLOR_PRESETS} from "../sticky-note/constants"
import {useTextWidgetUpdate} from './useTextWidgetUpdate'
import './TextWidgetFullEditor.less'

interface Props {
    canvasId: string
    widgetId: string
    data: TextWidgetData
    toolbarStyles: CSSProperties
    onClose: () => void
}

export const TextWidgetFullEditor: FC<Props> = ({
    canvasId,
    widgetId,
    data,
    toolbarStyles,
    onClose,
}) => {
    const [editingText, setEditingText] = useState(data.text)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    useEffect(() => {
        setEditingText(data.text)
    }, [data.text])

    const { updateData } = useTextWidgetUpdate(canvasId, widgetId, data)

    const handleBgColorChange = (color: string) => {
        updateData(['bgColor'], {bgColor: color})
    }

    const handleFgColorChange = (color: string) => {
        updateData(['fgColor'], {fgColor: color})
    }

    const handleHorizontalAlignChange = (horizontalTextAlign: HorizontalTextAlign) => {
        updateData(['textProperties.horizontalTextAlign'], {
            textProperties: {...data.textProperties, horizontalTextAlign}
        })
    }

    const handleVerticalAlignChange = (verticalTextAlign: VerticalTextAlign) => {
        updateData(['textProperties.verticalTextAlign'], {
            textProperties: {...data.textProperties, verticalTextAlign}
        })
    }

    const handleFormatChange = (properties: { bold?: boolean; italic?: boolean; underline?: boolean; lineThrough?: boolean }) => {
        const updatedProperties = {...data.textProperties, ...properties}
        const patchedPaths = Object.keys(properties).map(prop => `textProperties.${prop}`)
        updateData(patchedPaths, {textProperties: updatedProperties})
    }

    const handleFontSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fontSize = parseInt(e.target.value, 10)
        if (isNaN(fontSize) || fontSize < 1) return
        updateData(['textProperties.fontSize'], {
            textProperties: {...data.textProperties, fontSize}
        })
    }

    const saveText = () => {
        if (editingText !== data.text) {
            updateData(['text'], {text: editingText})
        }
    }

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditingText(e.target.value)
    }

    const handleTextKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Escape') {
            onClose()
        } else if (e.key === 'Enter' && isModPressed(e)) {
            e.preventDefault()
            saveText()
        }
    }
    const closeEditor = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        event.preventDefault()
        onClose()
    }

    return (
        <div className='text-widget-full-editor' >
            <div className='text-widget-full-editor__header'>
                <span className='text-widget-full-editor__title'>Text Widget Properties</span>
                <button
                    className='text-widget-full-editor__close'
                    onMouseDown={closeEditor}
                    aria-label='Close full editor'
                    type='button'
                >
                    ×
                </button>
            </div>

            <div className='text-widget-full-editor__sections'>
                <div className='text-widget-full-editor__section text-widget-full-editor__section--color' title='Background'>
                    <ColorPicker
                        value={data.bgColor}
                        presets={COLOR_PRESETS}
                        onChange={handleBgColorChange}
                    />
                </div>

                <div className='text-widget-full-editor__section text-widget-full-editor__section--color' title='Text Color'>
                    <ColorPicker
                        value={data.fgColor || '#333333'}
                        presets={COLOR_PRESETS}
                        onChange={handleFgColorChange}
                    />
                </div>

                <div className='text-widget-full-editor__section text-widget-full-editor__section--align' title='Align'>
                    <TextAlignmentPicker
                        horizontalAlign={data.textProperties.horizontalTextAlign}
                        verticalAlign={data.textProperties.verticalTextAlign}
                        onHorizontalAlignChange={handleHorizontalAlignChange}
                        onVerticalAlignChange={handleVerticalAlignChange}
                    />
                </div>

                <div className='text-widget-full-editor__section text-widget-full-editor__section--format' title='Format'>
                    <TextFormatPicker
                        prefix="text-widget-full-editor"
                        bold={data.textProperties.bold}
                        italic={data.textProperties.italic}
                        underline={data.textProperties.underline}
                        lineThrough={data.textProperties.lineThrough}
                        onChange={handleFormatChange}
                    />
                </div>

                <div className='text-widget-full-editor__section text-widget-full-editor__section--font-size' title='Font Size'>
                    <input
                        className='text-widget-full-editor__font-size-input'
                        type='number'
                        min={8}
                        max={200}
                        value={data.textProperties.fontSize || 14}
                        onChange={handleFontSizeChange}
                    />
                </div>

                <div className='text-widget-full-editor__section text-widget-full-editor__section--text' title='Text'>
                    <textarea
                        ref={textareaRef}
                        className='text-widget-full-editor__textarea'
                        value={editingText}
                        onChange={handleTextChange}
                        onKeyDown={handleTextKeyDown}
                        onBlur={saveText}
                        rows={3}
                    />
                </div>
            </div>
        </div>
    )
}
